import type { CSSProperties, ReactNode } from 'react';

import cn from 'classnames';

import type { StampCardStamp } from '../../../../../stores/stamp-card/types';
import { STAMP_CARD_TEST_ID_PREFIX, STAMP_CARD_WIDGET_LOGO } from '../../../constants';
import { StampCardStampsRow } from '../StampCardStampsRow/StampCardStampsRow';

import styles from './StampCardSmallWidget.module.scss';

const TEST_ID_PREFIX = `${STAMP_CARD_TEST_ID_PREFIX}-widget-small`;

interface StampCardSmallWidgetProps {
  current: number;
  target: number;
  stamps: StampCardStamp[];
  timer?: ReactNode;
  timerExtended?: ReactNode;
  backgroundUrl?: string | null;
  wideBackgroundUrl?: string | null;
  logoAssetUrl?: string | null;
  emptyStampAssetUrl?: string | null;
  filledStampAssetUrl?: string | null;
  lockedStampAssetUrl?: string | null;
  isOpening: boolean;
  isClosing: boolean;
  isPoppingOut: boolean;
  isRotatingOnClose: boolean;
  isBottomRevealing: boolean;
  popDurationMs: number;
  justStampedIndexes?: number[];
  justUnlockedIndex?: number | null;
}

export function StampCardSmallWidget(props: StampCardSmallWidgetProps) {
  const {
    current,
    target,
    stamps,
    timer,
    timerExtended,
    backgroundUrl,
    wideBackgroundUrl,
    logoAssetUrl,
    emptyStampAssetUrl,
    filledStampAssetUrl,
    lockedStampAssetUrl,
    isOpening,
    isClosing,
    isPoppingOut,
    isRotatingOnClose,
    isBottomRevealing,
    popDurationMs,
    justStampedIndexes,
    justUnlockedIndex,
  } = props;
  const progressPercentage = target ? Math.min((current / target) * 100, 100) : 0;

  return (
    <div
      className={cn(
        styles.container,
        backgroundUrl && styles.noBorder,
        isPoppingOut && styles.containerOverflowVisible,
      )}
      data-testid={TEST_ID_PREFIX}
    >
      {backgroundUrl ? (
        <>
          <div
            className={cn(
              styles.wideBackground,
              isOpening && styles.wideBackgroundExpandAnimation,
              isClosing && styles.wideBackgroundOpenHeld,
              isPoppingOut && styles.wideBackgroundPopAnimation,
            )}
            style={
              {
                '--pop-duration': `${popDurationMs}ms`,
                ...(wideBackgroundUrl && (isOpening || isClosing)
                  ? {
                      backgroundImage: `url(${wideBackgroundUrl})`,
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                    }
                  : {}),
              } as CSSProperties
            }
          >
            <div className={styles.wideContent}>
              <StampCardStampsRow
                emptyStampAssetUrl={emptyStampAssetUrl}
                filledStampAssetUrl={filledStampAssetUrl}
                isClosing={isClosing}
                isOpening={isOpening}
                justStampedIndexes={justStampedIndexes}
                justUnlockedIndex={justUnlockedIndex}
                lockedStampAssetUrl={lockedStampAssetUrl}
                stamps={stamps}
                target={target}
                testIdPrefix={TEST_ID_PREFIX}
              />
              <div
                className={cn(
                  styles.timerReveal,
                  isOpening && styles.timerRevealAnimation,
                  isClosing && styles.timerRevealExitAnimation,
                )}
              >
                {timerExtended}
              </div>
            </div>
          </div>
          <img
            alt=""
            className={cn(
              styles.widgetImage,
              isOpening && styles.imageOpenAnimation,
              isRotatingOnClose && styles.imageCloseAnimation,
              (isOpening || (isClosing && !isRotatingOnClose)) && styles.imageCentered,
            )}
            src={backgroundUrl}
          />
        </>
      ) : (
        <div className={styles.topRow}>
          <img
            alt=""
            className={cn(styles.logo, isOpening && styles.imageOpenAnimation)}
            data-testid={`${TEST_ID_PREFIX}-logo`}
            src={logoAssetUrl || STAMP_CARD_WIDGET_LOGO}
          />
          <div className={styles.stampsReveal}>
            <StampCardStampsRow
              emptyStampAssetUrl={emptyStampAssetUrl}
              filledStampAssetUrl={filledStampAssetUrl}
              justStampedIndexes={justStampedIndexes}
              justUnlockedIndex={justUnlockedIndex}
              lockedStampAssetUrl={lockedStampAssetUrl}
              stamps={stamps}
              target={target}
              testIdPrefix={TEST_ID_PREFIX}
            />
          </div>
        </div>
      )}
      <div
        className={cn(
          styles.bottom,
          isOpening && styles.bottomOpenAnimation,
          isClosing && !isBottomRevealing && styles.bottomHiddenDuringClose,
          isBottomRevealing && styles.bottomCloseRevealAnimation,
        )}
      >
        <div className={styles.progressBarTrack}>
          <div className={styles.progressBarFillWrap}>
            <div className={styles.progressBarFill} style={{ width: `${progressPercentage}%` }} />
          </div>
          <span className={styles.progressBarText} data-testid={`${TEST_ID_PREFIX}-progress-text`}>
            {current}/{target}
          </span>
        </div>
        {timer && <div className={styles.timerBar}>{timer}</div>}
      </div>
    </div>
  );
}
